import type { MigrationScope, MigrationStage } from '@detect/shared';
import { api, withTenant, type PaginatedResult } from './api';
import type { WithId } from './types';

/**
 * Migration from an incumbent (#15). The API never returns credentials or
 * secret names: the connection view only says whether they are ON FILE.
 */

export type ScopeView = WithId<MigrationScope>;

export interface ConnectionView {
  id: string;
  vendor: 'logicmonitor';
  company: string;
  status: 'verified' | 'failed';
  lastVerifiedAt: string | null;
  lastError: string | null;
  capability: { read: boolean; write: 'unverified' | 'proven' | 'denied' };
  inventory: Record<string, number | null> | null;
  credentialsOnFile: boolean;
  writeCredentialOnFile: boolean;
}

export interface ReportItem {
  lmId: number;
  name: string;
  status: 'ready' | 'waiting-for-collector' | 'unmapped';
  reason?: string;
  lmCollectorId?: number | null;
  notes: string[];
}

export interface ImportReport {
  generatedAt: string;
  summary: Record<string, number | null>;
  truncated: { devices: boolean; websites: boolean; dataSources: boolean };
  cappedDevices: boolean;
  nextAfterDeviceId: number | null;
  cappedWebsites: boolean;
  nextAfterWebsiteId: number | null;
  missingDeviceIds: number[];
  devices: ReportItem[];
  websites: ReportItem[];
  dataSources: {
    lmDataSourceId: number;
    name: string;
    collectMethod: string;
    appliedToDevices: number;
    outcome: 'library' | 'proposal' | 'unmapped';
    reason?: string;
    notes: string[];
  }[];
  routing: {
    rules: { lmRuleId: number; blockers: string[] }[];
    chains: { lmChainId: number; blockers: string[] }[];
  };
}

export interface ImportRequest {
  dryRun: boolean;
  devices?: { allDevices: true } | { deviceGroupIds: number[] } | { deviceIds: number[] };
  includeWebsites: boolean;
  afterDeviceId?: number;
  afterWebsiteId?: number;
  collectorMap: Record<string, string>;
  clientId?: string;
}

export interface ImportResult {
  dryRun: boolean;
  report: ImportReport;
  plannedResources?: number;
  applied?: { created: number; alreadyImported: number; failed: { lm: { kind: string; id: number }; error: string }[] };
}

export interface TuneGroup {
  dataSourceId: number | null;
  dataSourceName: string | null;
  dataPointName: string | null;
  alerts: number;
  devices: number;
  alertsPerDeviceDay: number;
  medianDurationSeconds: number | null;
  ackedShare: number;
  threshold: string | null;
  flags: ('noisy' | 'flappy' | 'never-acked')[];
  suggestion: { operator: string; current: number; proposed: number; wouldHaveRemoved: number; basedOnAlerts: number } | null;
  notes: string[];
}
export interface TuneReport {
  sinceDays: number;
  alertsAnalysed: number;
  truncated: boolean;
  groupsTotal: number;
  groups: TuneGroup[];
}

export interface CompareDevice {
  lmDeviceId: number;
  comparedFrom: string | null;
  lmAlerts: number;
  detectAlerts: number;
  lmOnly: number;
  detectOnly: number;
  verdict: 'agree' | 'detect-missed' | 'detect-extra' | 'both';
  lmOnlyExamples: { startedAt: string; what: string; ongoing: boolean }[];
  detectOnlyExamples: { startedAt: string; what: string; ongoing: boolean }[];
}
export interface CompareReport {
  sinceDays: number;
  summary: {
    devicesCompared: number;
    devicesNotCompared: number;
    complete: boolean;
    devicesAgree: number;
    lmAlerts: number;
    detectAlerts: number;
    lmMatched: number;
    lmOnly: number;
    detectOnly: number;
    lmAlertsOnNotImported: number;
  };
  truncated: { lmAlerts: boolean; lmDevices: boolean; detectAlerts: boolean };
  devices: CompareDevice[];
  devicesTotalDivergent: number;
  notes: string[];
}

export interface CutoverStatus {
  phase: 'planned' | 'detect-live-incumbent-alerting' | 'complete' | 'reversing' | 'reversed' | null;
  stage: 'silent' | 'live' | MigrationStage;
  done?: boolean;
  note?: string | null;
  counts?: Record<string, number>;
  failures?: { lmDeviceId: number; error: string }[];
  startedAt?: string;
}

export const migrationService = {
  listScopes: (tenantId: string, params: { limit?: number; startAfter?: string } = {}): Promise<PaginatedResult<ScopeView>> =>
    api.getPaginated<ScopeView>(withTenant('/migration/scopes', tenantId, params)),
  getScope: (tenantId: string, id: string): Promise<ScopeView> => api.get<ScopeView>(withTenant(`/migration/scopes/${id}`, tenantId)),
  createScope: (tenantId: string, input: { name: string; selector: unknown }): Promise<ScopeView> =>
    api.post<ScopeView>(withTenant('/migration/scopes', tenantId), input),
  changeStage: (tenantId: string, id: string, to: MigrationStage): Promise<ScopeView> =>
    api.post<ScopeView>(withTenant(`/migration/scopes/${id}/stage`, tenantId), { to }),
  deleteScope: (tenantId: string, id: string): Promise<void> => api.delete(withTenant(`/migration/scopes/${id}`, tenantId)),

  getConnection: (tenantId: string): Promise<ConnectionView | null> => api.get<ConnectionView | null>(withTenant('/migration/connection', tenantId)),
  connect: (tenantId: string, input: { company: string; accessId: string; accessKey: string }): Promise<ConnectionView> =>
    api.put<ConnectionView>(withTenant('/migration/connection', tenantId), input),
  verify: (tenantId: string): Promise<ConnectionView> => api.post<ConnectionView>(withTenant('/migration/connection/verify', tenantId)),
  setWriteCredential: (tenantId: string, input: { accessId: string; accessKey: string }): Promise<ConnectionView> =>
    api.put<ConnectionView>(withTenant('/migration/connection/write-credential', tenantId), input),
  disconnect: (tenantId: string): Promise<void> => api.delete(withTenant('/migration/connection', tenantId)),

  importScope: (tenantId: string, id: string, input: ImportRequest): Promise<ImportResult> =>
    api.post<ImportResult>(withTenant(`/migration/scopes/${id}/import`, tenantId), input),
  tune: (tenantId: string, id: string, sinceDays: number): Promise<{ report: TuneReport }> =>
    api.post<{ report: TuneReport }>(withTenant(`/migration/scopes/${id}/tune`, tenantId), { sinceDays }),
  compare: (tenantId: string, id: string, sinceDays: number): Promise<{ report: CompareReport }> =>
    api.post<{ report: CompareReport }>(withTenant(`/migration/scopes/${id}/compare`, tenantId), { sinceDays }),
  cutoverStatus: (tenantId: string, id: string): Promise<CutoverStatus> =>
    api.get<CutoverStatus>(withTenant(`/migration/scopes/${id}/cutover`, tenantId)),
  cutover: (tenantId: string, id: string, confirm: string): Promise<CutoverStatus> =>
    api.post<CutoverStatus>(withTenant(`/migration/scopes/${id}/cutover`, tenantId), { confirm }),
  reverse: (tenantId: string, id: string, confirm: string): Promise<CutoverStatus> =>
    api.post<CutoverStatus>(withTenant(`/migration/scopes/${id}/reverse`, tenantId), { confirm }),
};
